/**
 * CBO Enrichment Dashboard
 * Organization-level view of enrichment coverage, scores, and activity.
 */

import React, { useState, useEffect } from 'react';
import {
  Brain,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  RefreshCw,
  Loader2,
  BarChart3,
  Shield,
  Heart,
  Activity,
  Zap,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  memberEnrichmentAPI,
  EnrichmentDashboard as DashboardData,
  RetentionDashboardSummary,
} from '@/services/api/memberEnrichment';

export default function EnrichmentDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [retention, setRetention] = useState<RetentionDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [batchRunning, setBatchRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const cboId = parseInt(localStorage.getItem('active_cbo_id') || '0', 10);
      const [d, r] = await Promise.allSettled([
        memberEnrichmentAPI.getDashboard(),
        cboId ? memberEnrichmentAPI.getRetentionDashboard(cboId) : Promise.resolve(null),
      ]);
      if (d.status === 'fulfilled') setData(d.value);
      if (r.status === 'fulfilled') setRetention(r.value);
      setError(null);
    } catch (err: any) {
      setError('Failed to load enrichment dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDashboard(); }, []);

  const handleBatchEnrich = async () => {
    setBatchRunning(true);
    try {
      await memberEnrichmentAPI.enrichBatch(undefined, 100, 24);
      setTimeout(() => {
        loadDashboard();
        setBatchRunning(false);
      }, 3000);
    } catch {
      setBatchRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-500">Loading dashboard...</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Brain className="h-6 w-6 text-blue-500" />
              Member Data Enrichment Dashboard
            </h2>
          </div>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <Brain className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Enrichment System Initializing</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              The member enrichment database tables are being set up. Once the migration is applied, this dashboard
              will show enrichment coverage, quality scores, and activity across your members.
            </p>
            <Button variant="outline" size="sm" onClick={loadDashboard} className="mt-4 gap-1">
              <RefreshCw className="h-4 w-4" /> Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalMembers = data.total_members_enriched + data.never_enriched;
  const enrichedPct = totalMembers > 0 ? ((data.total_members_enriched / totalMembers) * 100).toFixed(0) : '0';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Brain className="h-6 w-6 text-blue-500" />
            Member Data Enrichment Dashboard
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Organization-wide member enrichment coverage and quality metrics
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadDashboard} className="gap-1">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
          <Button size="sm" onClick={handleBatchEnrich} disabled={batchRunning} className="gap-1">
            {batchRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            {batchRunning ? 'Running...' : 'Run Batch Enrichment'}
          </Button>
        </div>
      </div>

      {/* Coverage Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-5 w-5 text-blue-500 mx-auto mb-1" />
            <p className="text-2xl font-bold">{totalMembers}</p>
            <p className="text-xs text-gray-500">Total Members</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="h-5 w-5 text-green-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-green-600">{data.well_enriched}</p>
            <p className="text-xs text-gray-500">Well Enriched (70+)</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="h-5 w-5 text-yellow-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-yellow-600">{data.partially_enriched}</p>
            <p className="text-xs text-gray-500">Partially Enriched</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-red-600">{data.needs_enrichment}</p>
            <p className="text-xs text-gray-500">Needs Enrichment</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <XCircle className="h-5 w-5 text-gray-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-500">{data.never_enriched}</p>
            <p className="text-xs text-gray-500">Never Enriched</p>
          </CardContent>
        </Card>
      </div>

      {/* Coverage Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enrichment Coverage</span>
            <span className="text-sm font-bold text-blue-600">{enrichedPct}%</span>
          </div>
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
            {totalMembers > 0 && (
              <>
                <div
                  className="bg-green-500 h-full"
                  style={{ width: `${(data.well_enriched / totalMembers) * 100}%` }}
                />
                <div
                  className="bg-yellow-400 h-full"
                  style={{ width: `${(data.partially_enriched / totalMembers) * 100}%` }}
                />
                <div
                  className="bg-red-400 h-full"
                  style={{ width: `${(data.needs_enrichment / totalMembers) * 100}%` }}
                />
              </>
            )}
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Well Enriched</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" /> Partial</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> Needs Work</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-300" /> Not Enriched</span>
          </div>
        </CardContent>
      </Card>

      {/* Average Scores */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="h-4 w-4 text-blue-500" />
              <span className="text-xs text-gray-500">Avg Overall</span>
            </div>
            <p className="text-2xl font-bold">{data.avg_overall_score}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="h-4 w-4 text-green-500" />
              <span className="text-xs text-gray-500">Avg Data Quality</span>
            </div>
            <p className="text-2xl font-bold">{data.avg_data_quality}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Heart className="h-4 w-4 text-purple-500" />
              <span className="text-xs text-gray-500">Avg SDOH Risk</span>
            </div>
            <p className="text-2xl font-bold">{data.avg_sdoh_risk}</p>
            {data.high_sdoh_risk_count > 0 && (
              <Badge variant="destructive" className="text-xs mt-1">{data.high_sdoh_risk_count} high risk</Badge>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="h-4 w-4 text-orange-500" />
              <span className="text-xs text-gray-500">Avg Engagement</span>
            </div>
            <p className="text-2xl font-bold">{data.avg_engagement}</p>
          </CardContent>
        </Card>
      </div>

      {/* Retention Intelligence Metrics */}
      {retention && (
        <Card className="border-l-4 border-indigo-300 dark:border-indigo-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-500" /> Retention Intelligence
              <Badge variant="outline" className="text-xs ml-1">from retention engine</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center">
                <AlertTriangle className="h-5 w-5 text-yellow-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-yellow-600">{retention.at_risk_count}</p>
                <p className="text-xs text-gray-500">At Risk Members</p>
              </div>
              <div className="text-center">
                <XCircle className="h-5 w-5 text-red-500 mx-auto mb-1" />
                <p className="text-2xl font-bold text-red-600">{retention.critical_count}</p>
                <p className="text-xs text-gray-500">Critical Risk</p>
              </div>
              <div className="text-center">
                <TrendingUp className="h-5 w-5 text-blue-500 mx-auto mb-1" />
                <p className="text-2xl font-bold">{retention.avg_engagement.toFixed(0)}</p>
                <p className="text-xs text-gray-500">Avg Retention Engagement</p>
              </div>
              <div className="text-center">
                <Activity className="h-5 w-5 text-purple-500 mx-auto mb-1" />
                <p className="text-2xl font-bold">{retention.churn_predictions_count}</p>
                <p className="text-xs text-gray-500">Churn Predictions</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      {data.recent_activity && data.recent_activity.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" /> Recent Activity (7 days)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module</TableHead>
                  <TableHead className="text-right">Runs</TableHead>
                  <TableHead className="text-right">Avg Confidence</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent_activity.map((a) => (
                  <TableRow key={a.module}>
                    <TableCell className="font-medium text-sm">{a.module.replace(/_/g, ' ')}</TableCell>
                    <TableCell className="text-right">{a.runs_7d}</TableCell>
                    <TableCell className="text-right">{(a.avg_confidence * 100).toFixed(0)}%</TableCell>
                    <TableCell className="text-right">${a.cost_7d.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Total Issues */}
      {data.total_issues_found > 0 && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {data.total_issues_found} total issues found across all enriched members. Review individual member profiles for details.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

export { EnrichmentDashboard };
